// Fase 15A — Gestione Studi / Organizzazioni consulenziali (punti 7-9, 173).
import { useCallback, useEffect, useState } from "react";
import { Building2, Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ORG_STATUS_STYLE = {
  ACTIVE: "bg-green-50 text-green-800 border-green-300",
  PENDING: "bg-amber-50 text-amber-800 border-amber-300",
  SUSPENDED: "bg-red-50 text-red-800 border-red-300",
  ARCHIVED: "bg-slate-100 text-slate-500 border-slate-300",
};

export default function PlatformOrganizations() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [form, setForm] = useState({ name: "", legal_name: "", vat_number: "", country: "IT" });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/platform/organizations", {
        params: { search: search || undefined, status: status || undefined } });
      setData(d);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [search, status]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/platform/organizations", form);
      toast.success("Studio creato", { description: form.name });
      setForm({ name: "", legal_name: "", vat_number: "", country: "IT" });
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const setOrgStatus = async (org, s) => {
    try {
      await api.put(`/admin/platform/organizations/${org.id}/status`, null,
        { params: { status: s } });
      toast.success(`Studio ${org.name} → ${s}`);
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const deleteOrg = async (org) => {
    const confirm = window.prompt(
      `Cancellazione DEFINITIVA dello studio "${org.name}": verranno eliminati i suoi consulenti, inviti e assegnazioni (le aziende clienti restano). Digita il nome esatto dello studio per confermare:`);
    if (confirm === null) return;
    try {
      const { data } = await api.delete(`/admin/platform/organizations/${org.id}`, { params: { confirm } });
      toast.success(`Studio eliminato`, { description: `${data.consultants} consulenti, ${data.companies_unlinked} aziende scollegate` });
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const deleteAll = async () => {
    const confirm = window.prompt(
      'Cancellazione DEFINITIVA di TUTTI gli studi (consulenti, inviti, assegnazioni). Le aziende clienti restano ma vengono scollegate. Digita "ELIMINA TUTTI GLI STUDI" per confermare:');
    if (confirm === null) return;
    try {
      const { data } = await api.delete("/admin/platform/organizations", { params: { confirm } });
      toast.success(`${data.organizations} studi eliminati`, { description: `${data.consultants} consulenti rimossi` });
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  if (error) return <p className="text-sm text-red-700" data-testid="orgs-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-600" data-testid="orgs-loading">Caricamento…</p>;

  return (
    <div className="space-y-6" data-testid="platform-orgs-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Building2 className="w-7 h-7 text-green-800" aria-hidden="true" /> Studi / Organizzazioni
        </h1>
        <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50"
          onClick={deleteAll} data-testid="org-delete-all">
          <Trash2 className="w-4 h-4 mr-1" aria-hidden="true" /> Elimina tutti gli studi
        </Button>
      </div>

      <Card className="border-slate-200" data-testid="org-create-card">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
          <Plus className="w-4 h-4" aria-hidden="true" /> Nuovo Studio
        </CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label htmlFor="org-name">Nome studio *</Label>
              <Input id="org-name" required value={form.name} data-testid="org-name-input"
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="org-legal">Ragione sociale</Label>
              <Input id="org-legal" value={form.legal_name} data-testid="org-legal-input"
                onChange={(e) => setForm((f) => ({ ...f, legal_name: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="org-vat">P.IVA</Label>
              <Input id="org-vat" value={form.vat_number} data-testid="org-vat-input"
                onChange={(e) => setForm((f) => ({ ...f, vat_number: e.target.value }))} />
            </div>
            <div className="flex items-end">
              <Button type="submit" className="bg-green-700 hover:bg-green-800"
                disabled={!form.name} data-testid="org-create-submit">Crea studio</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2">
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per nome o P.IVA…" className="w-64 h-9"
          data-testid="orgs-search-input" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 h-9" data-testid="orgs-status-filter">
            <SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
          <SelectContent>
            {["ACTIVE", "PENDING", "SUSPENDED", "ARCHIVED"].map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2" data-testid="orgs-list">
        {data.items.length === 0 && (
          <p className="text-sm text-slate-500">Nessuno studio trovato.</p>)}
        {data.items.map((o) => (
          <div key={o.id} className="rounded-lg border border-slate-200 p-3 flex flex-wrap items-center gap-3"
            data-testid={`org-${o.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900">{o.name}
                <span className="text-[11px] text-slate-400 ml-2">{o.code}</span></p>
              <p className="text-[11px] text-slate-500">
                {o.vat_number || "—"} · {o.consultants_count} consulenti · lingua {o.default_language}
              </p>
            </div>
            <Badge variant="outline" className={ORG_STATUS_STYLE[o.status]}
              data-testid={`org-status-${o.id}`}>{o.status}</Badge>
            <Button size="sm" variant="ghost" className="h-8 text-xs text-red-700 hover:bg-red-50"
              onClick={() => deleteOrg(o)} data-testid={`org-delete-${o.id}`}>
              <Trash2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Elimina
            </Button>
            {o.status !== "ARCHIVED" && (
              <Select onValueChange={(v) => setOrgStatus(o, v)}>
                <SelectTrigger className="h-8 w-40 text-xs" data-testid={`org-status-select-${o.id}`}>
                  <SelectValue placeholder="Cambia stato…" /></SelectTrigger>
                <SelectContent>
                  {["ACTIVE", "SUSPENDED", "ARCHIVED"].filter((s) => s !== o.status).map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
